import React, {
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { i18nInstance } from 'i18n';
import { useTranslation } from 'react-i18next';

import { termsAndConditions } from 'api/cms/termsAndConditions';
import { updatePlayerTerms } from 'api/profile';
import { ModalDefaultProps } from 'components/Modal';
import { Spinner } from 'components/Spinner';
import { ModalTemplate } from 'components/ModalTemplate';
import { ModalTemplateSettings } from 'components/ModalTemplate/types';
import { CommonUtilsContext } from 'contexts/commonUtils';
import { AuthContext, useApi, User, uuid } from '@repo-level/core-front';

import { ScStaticPage } from 'components/StaticModal/styled';
import { Button } from 'components/buttons';
import { FormGroup } from 'components/form/groups/FormGroup';
import { Checkbox } from 'components/form/Checkbox';
import { AnalyticsEventName } from 'api/analytics';
import { Form } from 'informed';
import { useAnalytics } from 'helpers/analytics';
import { REASON_FOR_UPDATING_USER } from '@repo-level/core-front/contexts/auth/reducer';
import { ScNewTermsAndConditionsModal } from './styled';
import { useIsAustralia } from 'hooks/useIsAustralia';
import { Locale } from 'languages';

export const NewTermsAndConditionsModal = forwardRef<
  HTMLDivElement,
  ModalDefaultProps
>(({ onClose }, ref) => {
  const { t } = useTranslation();
  const { language } = i18nInstance;

  const { isMobileS } = useContext(CommonUtilsContext);
  const { sendEventToAnalytics } = useAnalytics();
  const { fetchUser } = useContext(AuthContext);
  const isAustralia = useIsAustralia();

  const [termsRequest, { pending, data }] = useApi(termsAndConditions);
  const [termsAccepted, setTermsAccepted] = useState(false);

  type AuthFieldsUser = Pick<User, 'authFieldsMissed'>;

  const modalSettings: ModalTemplateSettings = useMemo(() => {
    return isMobileS
      ? { type: 'mobile', mode: { type: 'bottom' } }
      : {
          type: 'desktop',
          size: 'large',
          mode: { type: 'default' },
        };
  }, [isMobileS]);

  useEffect(() => {
    termsRequest(isAustralia ? Locale.ENGLISH_AUSTRALIA : language);
  }, [termsRequest, language, isAustralia]);

  const [requestUpdatePlayer, { pending: pendingUpdatePlayer }] = useApi(
    updatePlayerTerms
  );

  const handleSubmit = async () => {
    const { request } = await requestUpdatePlayer({
      terms_acceptance: termsAccepted,
    });
    const { error } = await request;

    await fetchUser(REASON_FOR_UPDATING_USER.REFRESH_STATUSES);

    sendEventToAnalytics({
      name: AnalyticsEventName.MODAL__TERMS_AND_SERVICES__AGREE_BUTTON__CLICK,
      attributes: {
        additionalData: JSON.stringify({
          termsAccepted,
        }),
      },
    });

    if (error) {
      const errorJSON = JSON.stringify(error);
      sendEventToAnalytics({
        name:
          AnalyticsEventName.MODAL__TERMS_AND_SERVICES__AGREE_BUTTON__CLICK__ERROR,
        attributes: { additionalData: errorJSON },
      });
    }

    !error && onClose && onClose();
  };

  const handleChangeTerms = useCallback(() => {
    setTermsAccepted((prevState) => {
      const newState = !prevState;

      sendEventToAnalytics({
        name: AnalyticsEventName.MODAL__TERMS_AND_SERVICES__SUCCESS,
        attributes: {
          additionalData: JSON.stringify({
            termsAccepted: newState,
          }),
        },
      });

      return newState;
    });
  }, [sendEventToAnalytics]);

  if (pending) {
    return <Spinner size={64} absoluteCenter />;
  }

  if (!data) {
    return null;
  }

  return (
    <ModalTemplate
      ref={ref}
      settings={modalSettings}
      title={t('common:newTermsAndConditions.title')}
      isButtonCrossHidden
    >
      <ScNewTermsAndConditionsModal>
        <Form<AuthFieldsUser> onSubmit={handleSubmit}>
          <div className="text-block">
            <ScStaticPage>
              <div dangerouslySetInnerHTML={{ __html: data.text }} />
            </ScStaticPage>
          </div>
          <div className="checkbox-block">
            <FormGroup>
              <Checkbox
                key={uuid()}
                label={t('common:newTermsAndConditions.checkbox')}
                field="terms_acceptance"
                initialValue={termsAccepted}
                onClick={handleChangeTerms}
              />
            </FormGroup>
          </div>
          <div className="button-block">
            <Button
              green
              xLarge
              wide
              disabled={!termsAccepted}
              pending={pendingUpdatePlayer}
              type="submit"
            >
              {t('common:newTermsAndConditions.button')}
            </Button>
          </div>
        </Form>
      </ScNewTermsAndConditionsModal>
    </ModalTemplate>
  );
});
